import { useRef, useState } from "react";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";

export const ProductCard = ({ product }) => {
  const { lang, t, format } = useLang();
  const { add } = useCart();
  const ref = useRef(null);
  const [added, setAdded] = useState(false);

  const onMove = (e) => {
    const el = ref.current;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.setProperty("--ry", `${(px - 0.5) * 14}deg`);
    el.style.setProperty("--rx", `${(0.5 - py) * 14}deg`);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
    el.style.setProperty("--s", "1.02");
  };
  const onLeave = () => {
    const el = ref.current;
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--s", "1");
  };

  const handleAdd = () => {
    add(product);
    setAdded(true);
    toast.success(`${product.name[lang]} ${t.cart.addedToast}`);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <article
      ref={ref}
      className="tilt-card group"
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      data-testid={`product-card-${product.id}`}
    >
      <div className="sheen" />
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={product.image} alt={product.name[lang]} className="card-img w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/30 to-transparent" />
        <span className="absolute top-4 left-4 font-mono text-[0.62rem] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full glass text-[#7df9ff]">
          {product.tag[lang]}
        </span>
        <span className="absolute top-4 right-4 font-mono text-[0.62rem] tracking-[0.18em] uppercase text-[#666]">
          {product.type === "service" ? t.products.services : t.products.hardware}
        </span>
      </div>

      <div className="p-6 lift relative z-[3]">
        <h3 className="font-display font-bold text-base text-[#f4f2ec] tracking-tight" data-testid={`product-name-${product.id}`}>
          {product.name[lang]}
        </h3>
        <p className="mt-2 text-sm text-[#8a8a8a] leading-relaxed min-h-[40px]">{product.desc[lang]}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {product.specs.map((s) => (
            <span key={s} className="font-mono text-[0.6rem] tracking-wider px-2 py-1 rounded border border-white/[0.08] text-[#777]">
              {s}
            </span>
          ))}
        </div>
        <div className="mt-6 flex items-end justify-between gap-4">
          <div>
            <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[#666]">{t.products.from}</div>
            <div className="font-display font-bold text-xl text-gold-gradient" data-testid={`product-price-${product.id}`}>
              {format(product.price)}
            </div>
          </div>
          <button
            className={`btn-add ${added ? "added" : ""}`}
            onClick={handleAdd}
            data-testid={`add-to-cart-${product.id}`}
          >
            {added ? <Check size={14} /> : <Plus size={14} />}
            {added ? t.products.added : t.products.add}
          </button>
        </div>
      </div>
    </article>
  );
};
